import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { brand } from "../../theme/businessFlowsTheme";
import { Hint, Refusal, Toast, focusRing } from "../workspaces/WorkspaceUi";
import { canPlatform } from "../../utils/permissions";
import { getAdminFeatureFlags, setAdminFeatureFlag } from "../../actions";

// Console → Features: which of the product's features this instance offers.
//
// One switch per feature, for everyone on this instance at once. Off means
// gone - no tile, no menu item, no address and no endpoint - never a greyed
// button that explains itself. Nothing behind a switch is deleted: what a plan
// or a workspace granted is kept and applies again the moment it goes back on.
//
// Reading needs platform.view (so Support can see how an instance is set up,
// as on Plans and entitlements and Activity); changing needs settings.manage,
// which is what the backend asks for on POST /api/admin/features. Without it
// the switches are readable and fixed, rather than absent.

// Said once, above the list, rather than on every row.
const EVERYONE = "Every switch here applies to everyone on this instance, and takes effect on their next page load.";

export default function FeatureSwitches({ user = {} }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const mayChange = canPlatform(user, "settings.manage");

  useEffect(() => {
    let live = true;
    getAdminFeatureFlags().then((answer) => {
      if (!live) return;
      setState(answer);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, []);

  const toggle = async (feature) => {
    const value = !state.flags[feature.key];
    setSaving(feature.key);
    setError("");
    const answer = await setAdminFeatureFlag(feature.key, value);
    setSaving(null);
    if (!answer?.success) {
      setError(answer?.msg || "That couldn't be saved. Try again.");
      return;
    }
    // The server answers with the whole picture, so the row can never drift
    // from what it decided.
    if (answer.content) setState(answer.content);
    else setState((prev) => ({ ...prev, flags: { ...prev.flags, [feature.key]: value } }));
    setNotice(`${feature.label} is ${value ? "on" : "off"} for everyone on this instance.`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const features = state?.features || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 880, ...focusRing }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
        <TuneRoundedIcon sx={{ color: brand.signalDeep }} />
        <Typography variant="h6" component="h1" sx={{ fontWeight: 700 }}>
          Features
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What this instance offers. {EVERYONE}
      </Typography>

      {!mayChange && (
        <Hint title="You can see this, not change it" sx={{ mb: 2 }}>
          Changing what an instance offers is a bpExperts admin's decision.
        </Hint>
      )}

      {error && (
        <Refusal onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Refusal>
      )}

      {features.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          The server didn't answer with its features. Reload the page, or quote the reference on the error screen.
        </Typography>
      )}

      <Stack spacing={1.5}>
        {features.map((feature) => {
          const on = Boolean(state.flags?.[feature.key]);
          const blocked = Boolean(state.envBlocked?.[feature.key]);
          return (
            <Paper
              key={feature.key}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${brand.line}`,
                bgcolor: on ? "#fff" : brand.shell,
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 650 }}>{feature.label}</Typography>
                    <Chip
                      size="small"
                      label={on ? "On" : "Off"}
                      sx={{
                        fontWeight: 600,
                        bgcolor: on ? brand.signal : "transparent",
                        color: brand.ink,
                        border: on ? "none" : `1px solid ${brand.line}`,
                      }}
                    />
                    {blocked && (
                      <Chip
                        size="small"
                        icon={<LockOutlinedIcon sx={{ fontSize: 16 }} />}
                        label="Blocked on this server"
                        sx={{ fontWeight: 600, border: `1px solid ${brand.line}`, bgcolor: "transparent" }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {feature.summary}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    <strong>Off hides:</strong> {feature.hides}
                  </Typography>
                  {blocked && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      This server sets <code>{feature.env}=false</code>, so it can't be switched on from here. Change it where
                      the server is deployed.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: { sm: 0.25 } }}>
                  {saving === feature.key ? (
                    <CircularProgress size={22} />
                  ) : (
                    <Switch
                      checked={on}
                      disabled={!mayChange || blocked}
                      onChange={() => toggle(feature)}
                      inputProps={{ "aria-label": `${feature.label} on this instance` }}
                    />
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Toast notice={notice} onClose={() => setNotice("")} />
    </Box>
  );
}
